import { useState } from "react";
import { Link, useLocation } from "react-router";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { UserCircleIcon } from "@heroicons/react/20/solid";
import useUserStore from "../../store/useUserStore";
import useFeatureFlagStore from "../../store/useFeatureFlagStore";
import UserMenu from "./UserMenu";
import WithPermission from "../withPermission/WithPermission";
import { PERMISSIONS } from "../../utils/constants";
import { useAuctionUrls, getAuctionUrl } from "../../hooks/useAuctionUrls";

const navigation = [
  { name: "Watchlist", href: "/watchlist", requiresAuth: true },
  { name: "Auctions", href: "/auctions" },
];

export default function Header() {
  const { pathname } = useLocation();
  const { isAuthenticated = false, userName = "", logout, refreshToken } = useUserStore();
  const { enableOnlineAuction } = useFeatureFlagStore();
  const { auctionUrls } = useAuctionUrls();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);

  const slugs = pathname.slice(1).split("/");
  let showEventActions = ["auction", "auctioneer", "ringman", "presenter", "settings"].includes(slugs[0]);
  const showOnlineAuctionActions = ["online-auction"].includes(slugs[0]);
  const event_ID = slugs[1];

  return (
    <>
      <header className="bg-white shadow-sm z-110">
        <nav
          className="mx-auto flex max-w-(--breakpoint-2xl) items-center justify-between px-4 lg:px-6 h-16"
          aria-label="Global"
        >
          <Link to="/auctions" className="-m-1.5 p-1.5">
            <span className="sr-only">{import.meta.env.VITE_COMPANY_NAME}</span>
            <img
              className="h-8 w-auto"
              src={`/${import.meta.env.VITE_LOGO}`}
              alt={`${import.meta.env.VITE_COMPANY_NAME}`}
            />
          </Link>
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="hidden lg:flex lg:gap-x-12">
            {navigation
              .filter((item) => !item.requiresAuth || isAuthenticated)
              .map((item) => (
                <Link key={item.name} to={item.href} className="text-sm font-semibold text-gray-900 flex items-center">
                  {item.name}
                </Link>
              ))}
            {isAuthenticated ? (
              <UserMenu />
            ) : (
              <Link
                to="/login"
                className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                Login
              </Link>
            )}
          </div>
        </nav>
        <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
          <div className="fixed inset-0 z-20" />
          <DialogPanel className="fixed inset-y-0 right-0 z-120 w-full overflow-y-auto bg-white px-6 pb-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between h-16">
              <Link to="/auctions" className="-m-1.5 p-1.5" onClick={() => setMobileMenuOpen(false)}>
                <span className="sr-only">{import.meta.env.VITE_COMPANY_NAME}</span>
                <img
                  className="h-8 w-auto"
                  src={`/${import.meta.env.VITE_LOGO}`}
                  alt={`${import.meta.env.VITE_COMPANY_NAME}`}
                />
              </Link>
              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {[...navigation]
                    .reverse()
                    .filter((item) => !item.requiresAuth || isAuthenticated)
                    .map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}

                  {showOnlineAuctionActions && isAuthenticated && enableOnlineAuction && (
                    <>
                      <WithPermission id={PERMISSIONS.Auction_Auctioneer}>
                        <Link
                          to={`/auction-control-center/${event_ID}`}
                          target="_blank"
                          className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Auction Control Center (BETA)
                        </Link>
                      </WithPermission>
                    </>
                  )}
                  {isAuthenticated && showEventActions && (
                    <>
                      {slugs[0] !== "auction" && (
                        <Link
                          to={`/auction/${event_ID}`}
                          className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Live Event
                        </Link>
                      )}
                      <WithPermission id={PERMISSIONS.Auction_Auctioneer}>
                        <Link
                          to={`/auctioneer/${event_ID}`}
                          className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Auctioneer Board
                        </Link>
                      </WithPermission>
                      <WithPermission id={PERMISSIONS.Auction_AuctionClerk}>
                        <Link
                          to={`/ringman/${event_ID}`}
                          target="_blank"
                          className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Virtual Ringman
                        </Link>
                      </WithPermission>
                      <WithPermission id={PERMISSIONS.Auction_AuctionClerk}>
                        <Link
                          to={`/messenger/${event_ID}`}
                          target="_blank"
                          className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Messenger
                        </Link>
                      </WithPermission>
                      <WithPermission id={PERMISSIONS.Auction_Presenter}>
                        <Link
                          to={`/presenter/${event_ID}`}
                          target="_blank"
                          className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Presenter Board
                        </Link>
                      </WithPermission>
                      {slugs[0] !== "settings" && (
                        <WithPermission id={PERMISSIONS.Auction_AuctionClerk}>
                          <Link
                            to={`/settings/${event_ID}`}
                            target="_blank"
                            className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50`}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            Event Settings
                          </Link>
                        </WithPermission>
                      )}
                      <WithPermission id={PERMISSIONS.Auction_AuctionClerk}>
                        <Link
                          to={`/ringman/${event_ID}/video`}
                          target="_blank"
                          className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Broadcast Settings
                        </Link>
                      </WithPermission>
                    </>
                  )}
                </div>
                <div className="py-6 flex flex-col gap-4">
                  {isAuthenticated ? (
                    <>
                      <div className="flex items-center font-semibold text-primary-600 m-auto">
                        <UserCircleIcon className="h-6 mr-2" /> {userName}
                      </div>

                      {logoutModal ? (
                        <div>
                          <div className="bg-white rounded-t-lg">
                            <div className="sm:flex sm:items-start">
                              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                  Log out?
                                </DialogTitle>
                                <div className="mt-2">
                                  <p className="text-sm text-gray-500">Are you sure you want to log out?</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-lg">
                            <button
                              type="button"
                              className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
                              onClick={logout}
                            >
                              Log out
                            </button>
                            <button
                              type="button"
                              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                              onClick={() => setLogoutModal(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            className="block text-center w-full py-2.5 rounded-md bg-primary-600 text-sm font-semibold text-white shadow-xs hover:bg-primary-500 outline-hidden"
                            onClick={() => {
                              const auctionUrl = getAuctionUrl(auctionUrls, "myAccountURL");
                              const finalUrl = `${auctionUrl}?token=${refreshToken}`;
                              window.open(finalUrl, "_blank");
                            }}
                          >
                            My Account
                          </button>

                          <button
                            className="block text-center w-full py-2.5 rounded-md bg-primary-600 text-sm font-semibold text-white shadow-xs hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                            onClick={() => setLogoutModal(true)}
                          >
                            Log out
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <Link
                      to="/login"
                      className="block text-center py-2.5 rounded-md bg-primary-600 text-sm font-semibold text-white shadow-xs hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>
    </>
  );
}
